import type { BillableAction, SubscriptionTier } from "./constants";
import { SUBSCRIPTION_TIERS } from "./constants";

/**
 * One usage threshold a team has crossed that should gate dashboard access.
 *
 * `used` and `limit` are whole counts in the action's own unit (not
 * nanocredits). `limit` is the flat cumulative product threshold, not a
 * windowed/monthly cap.
 */
export interface UsageStatusExceededAction {
  action: BillableAction;
  used: number;
  limit: number;
  message: string;
}

/**
 * Backend-owned dashboard access decision for a team.
 *
 * The backend owns the policy; the frontend only applies `shouldBlockDashboard`
 * against its route allowlist (team settings and billing stay reachable). V0
 * covers only the cumulative proxied inference request threshold and carries no
 * reset timestamp because the block is lifetime, not windowed.
 */
export interface UsageStatus {
  status: "ok" | "blocked";
  shouldBlockDashboard: boolean;
  exceeded: UsageStatusExceededAction[];
}

export interface GetUsageStatusInput {
  teamId: string;
}

/**
 * Cumulative lifetime proxied inference requests a team may make before the
 * dashboard is blocked. This is intentionally a flat product threshold, not the
 * windowed monthly tier limit (which merely happens to share the 1,000,000
 * value on the free tier) — crossing it blocks the dashboard until the team's
 * plan changes, with no automatic monthly reset.
 */
export const PROXIED_INFERENCE_REQUESTS_LIFETIME_BLOCK_THRESHOLD = 1_000_000;

/**
 * Per-tier lifetime proxied-inference-request dashboard-block threshold.
 *
 * The lifetime block is a FREE-tier conversion gate ("upgrade your plan to
 * restore dashboard access") — it makes no sense once a team has already paid.
 * Free teams keep the flat {@link PROXIED_INFERENCE_REQUESTS_LIFETIME_BLOCK_THRESHOLD}
 * (1,000,000). Paid tiers (growth / enterprise, and custom plans which resolve
 * to enterprise via `getEffectivePlanTier`) map to `null` = never gated by the
 * lifetime block; their usage is governed by their windowed tier limits
 * (enforced on the write path), not this cumulative lifetime cap.
 *
 * Before this map, `getUsageStatus` compared the lifetime counter against the
 * flat 1M constant with no tier lookup, so upgraded growth/enterprise teams
 * stayed dashboard-blocked forever once their cumulative proxied count crossed
 * 1M — the exact "paying customer still blocked after upgrading" bug.
 */
export const PROXIED_INFERENCE_REQUESTS_LIFETIME_BLOCK_THRESHOLD_BY_TIER: Readonly<
  Record<SubscriptionTier, number | null>
> = {
  [SUBSCRIPTION_TIERS.FREE]:
    PROXIED_INFERENCE_REQUESTS_LIFETIME_BLOCK_THRESHOLD,
  [SUBSCRIPTION_TIERS.GROWTH]: null,
  [SUBSCRIPTION_TIERS.ENTERPRISE]: null,
};

/**
 * Resolve the lifetime proxied-inference block threshold for a tier. Returns
 * `null` when the tier is never gated by the lifetime block (all paid tiers).
 */
export function getLifetimeProxiedBlockThreshold(
  tier: SubscriptionTier,
): number | null {
  return PROXIED_INFERENCE_REQUESTS_LIFETIME_BLOCK_THRESHOLD_BY_TIER[tier];
}

/**
 * Thrown by the data-API usage gate when a blocked team calls a gated read or
 * write endpoint. The same `exceeded` policy that blurs the dashboard also
 * fails API/CLI data access — ingestion paths stay open and are never gated.
 *
 * Carried as the `cause` of the tRPC error so the worker's `errorFormatter`
 * can tag the response (`data.usageLimitExceeded`) and `responseMeta` can map
 * it to an HTTP 402 Payment Required, without leaning on a brittle message
 * match.
 */
export class UsageLimitBlockedError extends Error {
  readonly exceeded: UsageStatusExceededAction[];

  constructor(exceeded: UsageStatusExceededAction[]) {
    super(
      exceeded[0]?.message ??
        "This team has exceeded its plan usage limit. Upgrade your plan to restore access.",
    );
    this.name = "UsageLimitBlockedError";
    this.exceeded = exceeded;
    // Preserve `instanceof` across the transpiled `extends Error` target.
    Object.setPrototypeOf(this, UsageLimitBlockedError.prototype);
  }
}

export function isUsageLimitBlockedError(
  value: unknown,
): value is UsageLimitBlockedError {
  return value instanceof UsageLimitBlockedError;
}

export class PaymentMethodRequiredError extends Error {
  readonly teamId: string;

  constructor(teamId: string) {
    super(
      "Add a payment method to this team before using the API. Billing settings remain available so you can add a card.",
    );
    this.name = "PaymentMethodRequiredError";
    this.teamId = teamId;
    Object.setPrototypeOf(this, PaymentMethodRequiredError.prototype);
  }
}

export function isPaymentMethodRequiredError(
  value: unknown,
): value is PaymentMethodRequiredError {
  return value instanceof PaymentMethodRequiredError;
}

export class CheckoutRequiredError extends Error {
  readonly teamId: string;

  constructor(teamId: string) {
    super(
      "Add credits to this team before using the API. Billing settings remain available so you can check out.",
    );
    this.name = "CheckoutRequiredError";
    this.teamId = teamId;
    Object.setPrototypeOf(this, CheckoutRequiredError.prototype);
  }
}

export function isCheckoutRequiredError(
  value: unknown,
): value is CheckoutRequiredError {
  return value instanceof CheckoutRequiredError;
}
